import { useEffect, useRef, useCallback } from 'react';
import { getApiUrl } from '../../../configs/config';
import { LessonProgressService } from '../../../services/lesson-progress/lesson-progress.service';
import { useLessonPlayerStore } from '../../../utils/lessonPlayerStore';
import { Interaction, InteractionState } from '../types/lesson.types';

// Count Interactions Helper
export const countInteractions = (data: any): number => {
  let count = data.lesson_video_url ? 1 : 0;
  data.content.forEach((step: any) => {
    count++;
    if (step.quiz?.question) count++;
    if (step.teacher_summary) count++;
  });
  return count;
};

interface UseLessonSyncProps {
  lessonData: any;
  interactionStates: Record<string, InteractionState>;
  interactionStatesRef: React.MutableRefObject<Record<string, InteractionState>>;
  setInteractionStates: React.Dispatch<React.SetStateAction<Record<string, InteractionState>>>;
  setNames: React.Dispatch<React.SetStateAction<any>>;
  totalItems: number;
  interactions: Interaction[];
  currentInteractionIndex: number;
}

export const useLessonSync = ({
  lessonData,
  interactionStates,
  interactionStatesRef,
  setInteractionStates,
  setNames,
  totalItems,
  interactions,
  currentInteractionIndex
}: UseLessonSyncProps) => {
  const { clearProgress, mergeServerProgress, syncToDB, currentProgress } = useLessonPlayerStore();
  const progressService = useRef(new LessonProgressService()).current;
  const syncTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const getFilteredStates = useCallback(() => {
    const filtered: Record<string, Partial<InteractionState>> = {};
    Object.entries(interactionStatesRef.current).forEach(([id, state]) => {
      const entry: any = {};
      if (state.selectedOption) entry.selectedOption = state.selectedOption;
      if (state.startedAt) entry.startedAt = state.startedAt;
      if (state.completedAt) entry.completedAt = state.completedAt;
      if (state.pointsEarned) entry.pointsEarned = state.pointsEarned;
      if (state.wrongOptions) entry.wrongOptions = state.wrongOptions;
      if (state.attempts !== undefined) entry.attempts = state.attempts;
      
      if (Object.keys(entry).length > 0) {
        filtered[id] = entry;
      }
    });
    return filtered;
  }, [interactionStatesRef]);

  const debouncedSync = useCallback(() => {
    if (syncTimeoutRef.current) clearTimeout(syncTimeoutRef.current);
    const lessonId = lessonData?.id;
    if (!lessonId) return;

    syncTimeoutRef.current = setTimeout(() => {
      syncTimeoutRef.current = null;
      syncToDB(lessonId, totalItems, getFilteredStates());
    }, 5000);
  }, [lessonData?.id, totalItems, getFilteredStates, syncToDB]);

  // Flush any pending debounced sync when unmounting (e.g. SPA navigation via AI agent)
  useEffect(() => {
    return () => {
      if (syncTimeoutRef.current && lessonData?.id) {
        clearTimeout(syncTimeoutRef.current);
        syncTimeoutRef.current = null;
        syncToDB(lessonData.id, totalItems, getFilteredStates());
      }
    };
  }, [lessonData?.id, totalItems, getFilteredStates, syncToDB]);

  useEffect(() => {
    const handleUnload = () => {
      const lessonId = lessonData?.id;
      if (!currentProgress || !lessonId) return;

      // Stamp completedAt on the active interaction before sending
      const activeItem = interactions[currentInteractionIndex];
      const updatedStates = { ...getFilteredStates() };
      if (activeItem && updatedStates[activeItem.id]?.startedAt && !updatedStates[activeItem.id]?.completedAt) {
        updatedStates[activeItem.id] = { ...updatedStates[activeItem.id], completedAt: Date.now() };
      }
      
      const payload = JSON.stringify({
        lesson_id: lessonId,
        status: currentProgress.percentage >= 100 ? 2 : 1,
        percentage: currentProgress.percentage,
        last_interaction_id: currentProgress.lastCompletedInteractionId,
        completed_interaction_ids: currentProgress.completedInteractions,
        total_interactions: totalItems,
        interaction_states: updatedStates
      });
      
      const blob = new Blob([payload], { type: 'application/json' });
      navigator.sendBeacon(`${getApiUrl()}/lesson-progress/upsert`, blob);
    };

    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, [lessonData?.id, currentProgress, totalItems, getFilteredStates, interactions, currentInteractionIndex]);

  const handleResumeProgress = useCallback(async (
    lessonId: number,
    data: any,
    effectiveTopicId: number,
    effectiveLevelId: number,
    effectiveSubTopicId: number
  ) => {
    const serverProgress = await progressService.getProgress(lessonId);
    if (!serverProgress) return null;

    const totalCount = countInteractions(data);

    if (serverProgress.total_interactions !== totalCount) {
      clearProgress(effectiveTopicId, effectiveLevelId, effectiveSubTopicId);
      progressService.upsertProgress({
        lessonId,
        status: 0,
        percentage: 0,
        lastInteractionId: null,
        completedInteractionIds: [],
        totalInteractions: totalCount
      });
      return null;
    }

    mergeServerProgress(serverProgress, effectiveTopicId, effectiveLevelId, effectiveSubTopicId);

    const initialStates: Record<string, InteractionState> = {};
    
    if (serverProgress.interaction_states) {
      const flatInteractions: any[] = [];
      if (data.lesson_video_url) flatInteractions.push({ id: 'video', type: 'video' });
      data.content.forEach((step: any, sIdx: number) => {
        flatInteractions.push({ id: `step_${sIdx}_content`, type: 'content' });
        if (step.quiz && step.quiz.question) flatInteractions.push({ id: `step_${sIdx}_quiz`, type: 'quiz', quiz: step.quiz });
        if (step.teacher_summary) flatInteractions.push({ id: `step_${sIdx}_summary`, type: 'summary' });
      });

      Object.entries(serverProgress.interaction_states).forEach(([id, state]: [string, any]) => {
        const interaction = flatInteractions.find(i => i.id === id);
        if (state.selectedOption || state.startedAt || state.completedAt || state.pointsEarned || state.wrongOptions || state.attempts !== undefined) {
          initialStates[id] = {
            ...initialStates[id],
            selectedOption: state.selectedOption,
            startedAt: state.startedAt,
            completedAt: state.completedAt,
            pointsEarned: state.pointsEarned,
            wrongOptions: state.wrongOptions || [],
            attempts: state.attempts || 0,
            isAnswered: !!state.selectedOption,
            isCorrect: interaction?.type === 'quiz' ? state.selectedOption === interaction.quiz.correct_option : (!!state.selectedOption),
            isTitleDone: true,
            isContentDone: true,
            isChatDone: true
          };
        }
      });
    }

    serverProgress.completed_interaction_ids.forEach((id: string) => {
      initialStates[id] = {
        ...initialStates[id],
        isAnswered: true,
        isRewarded: true,
        isStepCompleteAwarded: true,
        isTitleDone: true,
        isContentDone: true,
        isChatDone: true
      };
    });

    setNames((prev: any) => {
      if (!prev.levelName || !prev.topicName) {
        return {
          ...prev,
          levelName: prev.levelName || serverProgress.level_name || '',
          topicName: prev.topicName || serverProgress.full_topic_label || '',
        };
      }
      return prev;
    });

    interactionStatesRef.current = initialStates;
    setInteractionStates(initialStates);
    return serverProgress;
  }, [clearProgress, mergeServerProgress, progressService, setInteractionStates, setNames, interactionStatesRef]);

  return {
    debouncedSync,
    getFilteredStates,
    handleResumeProgress
  };
};
// Agent synced
